import { nsHistorics } from '@/app/i18n/namespaces';
import Entity from '@/common-utils/components/renders/entity/Entity';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import PropTypes from 'prop-types';
import HISTORICS_VALUE_TYPE from '../../enums/HistoricsValueType';
import isFalsy from '@/common-utils/functions/isFalsy';
import { Badge } from '@/common-utils/components/ui/badge';
import RenderDateWithFromTime from '@/common-utils/components/renders/date/RenderDateWithFromTime';
import FooterClose from '@/common-utils/components/renders/modal/components/footers/FooterClose/FooterClose';
import UserPermissions from '@/app/modules/users/monetic/components/permissions/UserPermissions';
import Icon from '@/common-utils/components/renders/icon/Icon';
import ICONS from '@/common-utils/enums/icons';
import genUID from '@/common-utils/functions/generateUniqueId';


/**
 *
 * @param {Object} props
 * @param {Object} props.isOpen
 * @param {Function} props.setIsOpen
 * @param {Object} props.historics
 * @param {Object} props.actions
 * @returns {JSX} ModalHistoricsDetails
 */
function ModalHistoricsDetails({ isOpen, setIsOpen, historics, actions }) {
    // Constants
    const action = actions[historics.actionType];

    const renderHistoricDetail = ({ historicValue, historicValueBefore = null }) => {

        // Constants
        const NO_VALUE_LABEL = <Entity entity='modal.details.no_value' namespace={nsHistorics} className='italic text-xs' />;


        // Prevent null values from historicValue
        if (isFalsy(historicValue)) {
            return NO_VALUE_LABEL;
        }

        // FORMAT : {"UNKNOWN_TARGET_IDENTIFIER": VALUE(int, None) | {"users": None | LIST[str]}}
        if (historicValue.UNKNOWNTargetIdentifier || historicValue.UNKNOWNTargetIdentifier === 0) {
            const targetIdentifier = historicValue.UNKNOWNTargetIdentifier;
            if (typeof targetIdentifier === 'object') {
                return targetIdentifier.users
                    ? <Entity entity={targetIdentifier.users[0]} />
                    : NO_VALUE_LABEL;
            }
            return <Entity entity={targetIdentifier.toString()} />;
        }

        const cellObject = Object.values(historicValue)[0];
        if (Object.values(cellObject).length === 0) {
            return NO_VALUE_LABEL;
        }

        // FORMAT : {"UUID": {"KEY"(str): "VALUE"(str, int, list[str], None)}}
        const cellValue = Object.values(cellObject)[0];
        switch (action.type) {
            case HISTORICS_VALUE_TYPE.BADGE.name: {
                const badgeData = action.valuesList[cellValue];
                return <Badge entity={badgeData.entity} namespace={badgeData.namespace} variant={badgeData.variant} />;
            }
            case HISTORICS_VALUE_TYPE.DATE.name:
                return <RenderDateWithFromTime value={cellValue} />;
            case HISTORICS_VALUE_TYPE.LIST.name: {
                if (isFalsy(historicValueBefore)) {
                    return NO_VALUE_LABEL;
                }
                const initialPermissions = historicValueBefore && Object.values(historicValueBefore)[0];

                // Get the modified permissions list
                const initialList = initialPermissions[action.dataKeys[0]] || [];
                const finalList = cellObject[action.dataKeys[0]] || [];
                const removedPermissions = initialList.filter(x => !finalList.includes(x));
                const addedPermissions = finalList.filter(x => !initialList.includes(x));

                return <>
                    {!addedPermissions.length && !removedPermissions.length
                        && <Entity entity='cells.no_permission_modified' namespace={nsHistorics} className='italic' />
                    }
                    {addedPermissions.length > 0 &&
                        <>
                            <span className='flex'>
                                <Icon icon={ICONS.ADD} className='text-success-foreground' />
                                {addedPermissions.length}
                            </span>
                            <div className='max-h-80 w-full overflow-auto'>
                                <UserPermissions userPermissions={addedPermissions} isUpdate={false} />
                            </div>
                        </>
                    }
                    {removedPermissions.length > 0 &&
                        <>
                            <span className='flex'>
                                <Icon icon={ICONS.REMOVE} className='text-danger-foreground' />
                                {removedPermissions.length}
                            </span>
                            <div className='max-h-80 w-full overflow-auto'>
                                <UserPermissions userPermissions={removedPermissions} isUpdate={false} />
                            </div>
                        </>
                    }
                </>;
            }
            case HISTORICS_VALUE_TYPE.MULTI_VALUES.name: {
                return (
                    <ul className='mt-1'>
                        {Object.entries(cellObject).map(([key, value]) => {
                            const snakeCaseKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
                            return <li className='historics-list-item' key={genUID()}>
                                <Entity entity={`keys.${snakeCaseKey}`} namespace={action.namespace} />: {isFalsy(value) ? NO_VALUE_LABEL : `"${value}"`}
                            </li>;
                        })}
                    </ul>
                );
            }
            default:
                return cellValue ? <Entity entity={cellValue} /> : NO_VALUE_LABEL;
        }
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={'max-w-2xl'}>
                <HeaderTitle title={'modal.details.title'} namespace={nsHistorics} />
                <ModalBody className='flex flex-col gap-2'>
                    <div>
                        <Entity entity={'modal.details.action'} namespace={nsHistorics} className='font-bold' />
                        <Entity entity={actions[historics.actionType].entity} namespace={nsHistorics} className='mr-1' />
                    </div>
                    <div>
                        <Entity entity={'modal.details.initial'} namespace={nsHistorics} className='font-bold' />
                    </div>
                    <div>
                        {renderHistoricDetail({ historicValue: historics.attributesValuesBefore })}
                    </div>
                    <div>
                        <Entity entity={'modal.details.final'} namespace={nsHistorics} className='mr-2 font-bold' />
                    </div>
                    <div>
                        {renderHistoricDetail({ historicValue: historics.attributesValuesAfter, historicValueBefore: historics.attributesValuesBefore })}
                    </div>
                </ModalBody>
                <FooterClose />
            </ModalContent>
        </Modal>
    );
}

ModalHistoricsDetails.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    historics: PropTypes.object.isRequired,
    actions: PropTypes.object.isRequired
};

export default ModalHistoricsDetails;